/**
 * Littlemore Product Page Custom Elements
 * - <littlemore-sticky-cart>: Fixed bottom bar on mobile when hero buy button scrolls out of view
 * - <littlemore-quantity-input>: Styled +/- quantity buttons
 * - <littlemore-bundle-selector>: Bundle pack picker that drives the hidden quantity input
 */

class LittlemoreStickyCart extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.unsubscribe = null;
  }

  connectedCallback() {
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.buttonEl = this.querySelector('[data-sticky-button]');
    const targetSelector = this.dataset.observeTarget;
    const target = targetSelector ? document.querySelector(targetSelector) : null;

    if (target) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.classList.toggle('is-visible', !entry.isIntersecting);
          });
        },
        { threshold: 0 }
      );
      this.observer.observe(target);
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        this.updateFromVariant(event.data.variant);
      });
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.unsubscribe) this.unsubscribe();
  }

  updateFromVariant(variant) {
    if (!variant) return;

    if (this.priceEl) {
      const formatter = new Intl.NumberFormat(Shopify.locale, {
        style: 'currency',
        currency: Shopify.currency.active,
      });
      this.priceEl.textContent = formatter.format(variant.price / 100);
    }

    if (this.buttonEl) {
      this.buttonEl.disabled = !variant.available;
      const span = this.buttonEl.querySelector('span');
      if (span) {
        span.textContent = variant.available ? this.buttonEl.dataset.addText || 'Add to Cart' : 'Sold Out';
      }
    }
  }
}

class LittlemoreQuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    this.minusBtn = this.querySelector('[data-quantity-minus]');
    this.plusBtn = this.querySelector('[data-quantity-plus]');

    if (!this.input) return;

    this.minusBtn?.addEventListener('click', () => this.step(-1));
    this.plusBtn?.addEventListener('click', () => this.step(1));
  }

  step(direction) {
    const min = parseInt(this.input.min) || 1;
    const max = parseInt(this.input.max) || 9999;
    const step = parseInt(this.input.step) || 1;
    let value = parseInt(this.input.value) || min;

    value += direction * step;
    value = Math.max(min, Math.min(max, value));

    this.input.value = value;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

class LittlemoreBundleSelector extends HTMLElement {
  constructor() {
    super();
    this.unsubscribe = null;
    this.currentVariant = null;
  }

  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('.littlemore-product__bundle-card'));
    this.sectionId = this.dataset.section;
    this.quantityInput = this.sectionId ? document.getElementById('Quantity-' + this.sectionId) : null;

    const variantScript = this.sectionId
      ? document.querySelector('#variant-selects-' + this.sectionId + ' [data-selected-variant]')
      : document.querySelector('[data-selected-variant]');
    if (variantScript) {
      try {
        this.currentVariant = JSON.parse(variantScript.textContent);
      } catch (e) {
        this.currentVariant = null;
      }
    }

    this.cards.forEach((card) => {
      const radio = card.querySelector('input[type="radio"]');
      if (!radio) return;

      radio.addEventListener('change', () => this.selectCard(card));

      card.addEventListener('click', (event) => {
        if (event.target.tagName === 'INPUT') return;
        if (radio.checked) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    const initial =
      this.cards.find((card) => {
        const radio = card.querySelector('input[type="radio"]');
        return radio && radio.checked;
      }) || this.cards[0];

    if (initial) this.selectCard(initial);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event && event.data && event.data.variant) {
          this.currentVariant = event.data.variant;
          this.updateUnitPrices();
        }
      });
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  selectCard(card) {
    this.cards.forEach((c) => c.classList.toggle('is-selected', c === card));

    const qty = parseInt(card.dataset.bundleQuantity, 10) || 1;
    if (this.quantityInput) {
      this.quantityInput.value = qty;
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.updateUnitPrices();
  }

  updateUnitPrices() {
    if (!this.currentVariant || typeof this.currentVariant.price !== 'number') return;
    if (typeof Shopify === 'undefined' || !Shopify.currency) return;

    const formatter = new Intl.NumberFormat(Shopify.locale || 'en-US', {
      style: 'currency',
      currency: Shopify.currency.active,
    });

    const unitPrice = formatter.format(this.currentVariant.price / 100);

    this.cards.forEach((card) => {
      const display = card.querySelector('[data-bundle-unit-price]');
      if (display) display.textContent = unitPrice + ' each';
    });
  }
}

class LittlemoreProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.prevBtn = this.querySelector('[data-gallery-prev]');
    this.nextBtn = this.querySelector('[data-gallery-next]');

    if (this.slides.length === 0) return;

    this.activeIndex = 0;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = parseInt(thumb.dataset.index, 10) || 0;
        this.go(index);
      });
    });

    this.prevBtn?.addEventListener('click', () => this.go(this.activeIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.go(this.activeIndex + 1));
  }

  go(index) {
    const count = this.slides.length;
    if (count === 0) return;
    const next = ((index % count) + count) % count;

    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === next));
    this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === next));

    const activeThumb = this.thumbs[next];
    if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }

    this.activeIndex = next;
  }
}

customElements.define('littlemore-sticky-cart', LittlemoreStickyCart);
customElements.define('littlemore-quantity-input', LittlemoreQuantityInput);
customElements.define('littlemore-bundle-selector', LittlemoreBundleSelector);
customElements.define('littlemore-product-gallery', LittlemoreProductGallery);
